// @flow

/**
 * Module dependencies.
 */

import { Col, Row } from 'client/components/core/grid';
import { List, ListItem } from 'client/components/core/lists';
import { colors, columns, media, units } from 'client/styles';
import { compose } from 'recompose';
import { position, size } from 'polished';
import { prop } from 'styled-tools';
import { translator } from 'core/utils';
import Content from 'client/components/content';
import FormContact from 'client/components/forms/form-contact';
import Icon from 'client/components/core/icon';
import Layout from 'client/components/core/layout';
import React, { Component } from 'react';
import Text from 'client/components/core/type';
import styled from 'styled-components';
import zendeskEntities from 'client/core/entities/zendesk';

/**
 * `SuccessContent` type.
 */

type SuccessContent = {
  button: string,
  buttonThemeColor?: string,
  message: string,
  title: string
};

/**
 * `Props` type.
 */

type Props = {
  backgroundColor?: string,
  formSubmitLabel: string,
  formTitle: string,
  list: Array<string>,
  reason: string,
  successContent: SuccessContent,
  title: string,
  translate: Function
};

/**
 * `StyledList` component.
 */

const StyledList = styled(List)`
  margin-top: ${units(2)};

  ${media.min.md`
    margin-top: ${units(8)};
  `}
`;

/**
 * `Item` component.
 */

const Item = styled.div`
  color: ${({ color }) => color};
  margin: ${units(2)} 0;
  padding-left: ${units(5)};
  position: relative;

  span {
    ${position('absolute', '0', null, '0', '0')};
    ${size(units(3), units(3))};
  }
`;

/**
 * `VerticalAlignment` component.
 */

const VerticalAlignment = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
`;

/**
 * Overlay.
 */

const Overlay = styled.div`
  ${position('absolute', '0', '0', '50%', '0')}

  background-color: ${prop('bgColor', colors.white)};
  display: none;

  ${media.min.md`display: block;`}
`;

/**
 * `ColFormContainer`.
 */

const ColFormContainer = styled(Col)`
  @media (min-width: 1750px) {
    margin-left: ${columns(0)}%;
    width: ${columns(5)}%;
  }
`;

/**
 * `ColListContainer`.
 */

const ColListContainer = styled(Col)`
  @media (min-width: 1750px) {
    margin-left: ${columns(1)}%;
    width: ${columns(5)}%;
  }
`;

/**
 * `GetInTouch` component.
 */

class GetInTouch extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Render list items.
   */

  renderListItems(): React$Element<*> {
    const { list } = this.props;

    return (
      <StyledList>
        {list.map((item: string, index: number): React$Element<*> => (
          <ListItem
            block
            key={index}
          >
            <Item color={colors.white}>
              <Icon
                color={colors.white}
                icon={'check-circle'}
              />

              {item}
            </Item>
          </ListItem>
        ))}
      </StyledList>
    );
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const {
      backgroundColor,
      formSubmitLabel,
      formTitle,
      reason,
      successContent,
      title,
      translate
    } = this.props;

    const { mailSubjects } = zendeskEntities(translate);

    return (
      <Layout position={'relative'}>
        <Overlay bgColor={backgroundColor} />

        <Content
          background={colors.brandPrimaryDark}
          backgroundImage={'pattern-2'}
          pullRight
          roundedBorder
        >
          <Row>
            <VerticalAlignment>
              <ColListContainer
                md={4}
                mdOffset={2}
                xs={10}
                xsOffset={1}
              >
                <Layout
                  marginBottom={units(8)}
                  marginBottomMd={units(0)}
                >
                  <Text.H2 color={colors.white}>
                    {title}
                  </Text.H2>

                  {this.renderListItems()}
                </Layout>
              </ColListContainer>

              <ColFormContainer
                md={4}
                xs={10}
                xsOffset={1}
              >
                <FormContact
                  errorMessage={translate('formRequest.message.error')}
                  reason={reason}
                  subject={mailSubjects.contact}
                  subjectField={'reason'}
                  submitLabel={formSubmitLabel}
                  successContent={successContent}
                  title={formTitle}
                />
              </ColFormContainer>
            </VerticalAlignment>
          </Row>
        </Content>
      </Layout>
    );
  }

}

/**
 * Export `GetInTouch`.
 */

export default compose(
  translator()
)(GetInTouch);



// WEBPACK FOOTER //
// ../src/client/components/licensed-representatives/get-in-touch.js